import React, { useState } from "react";
import { Modal, Upload, message, Button, Tabs } from "antd";
import {
    InboxOutlined,
    FileTextOutlined,
    FileExcelOutlined,
    CloudUploadOutlined,
} from "@ant-design/icons";
import { observer } from "mobx-react";
import citationBulkUploadStore from "@app/state/store/citation/bulk-upload";
import { events } from "@app/lib/store";
import "./style/citation-modal.scoped.scss";

const { TabPane } = Tabs;

const { Dragger } = Upload;

const CitationBulkUploadModal = observer(() => {
    const [fileList, setFileList] = useState([]);
    const { modalState, projectId, activeTab } = citationBulkUploadStore;

    const handleOk = async () => {
        if (fileList.length === 0) {
            message.error("Please select files to upload");
            return;
        }

        // For RIS and Excel, only allow single file
        if (activeTab !== "pdf" && fileList.length > 1) {
            message.error("Please select only one file for RIS/Excel upload");
            return;
        }

        try {
            citationBulkUploadStore.toggleModal(false);
            await citationBulkUploadStore.upload(fileList, projectId, activeTab);
            setFileList([]);
        } catch (error) {
            message.error("Upload failed");
        } finally {
            events.emit("citations.update");
        }
    };

    const handleCancel = () => {
        citationBulkUploadStore.toggleModal(false);
        setFileList([]);
    };

    const onFileChange = ({ file, fileList }) => {
        const uniqueFiles = fileList.reduce((acc, current) => {
            const isDuplicate = acc.find(
                (item) => item.name === current.name && item.size === current.size,
            );
            if (!isDuplicate) {
                acc.push(current);
            }
            return acc;
        }, []);
        setFileList(uniqueFiles);
    };

    const handleTabChange = (key) => {
        citationBulkUploadStore.activeTab = key;
        setFileList([]);
    };

    const getUploadProps = (fileType) => ({
        accept: fileType === "pdf" ? "application/pdf" : fileType === "ris" ? ".ris" : ".xlsx,.xls",
        multiple: fileType === "pdf",
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            if (fileType !== "pdf" && fileList.length > 0) {
                message.error("Please select only one file for RIS/Excel upload");
                return false;
            }
            setFileList([...fileList, file]);
            return false;
        },
        fileList,
        onChange: onFileChange,
    });

    return (
        <Modal
            title="Bulk Upload Citations"
            visible={modalState.open}
            onOk={handleOk}
            onCancel={handleCancel}
            destroyOnClose={false}
            footer={[
                <Button key="back" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={handleOk}>
                    Upload
                </Button>,
            ]}
            className="citation-bulk-upload-modal"
        >
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                className="citation-bulk-upload-modal-tabs"
            >
                <TabPane
                    tab={
                        <span>
                            <CloudUploadOutlined />
                            PDF Files
                        </span>
                    }
                    key="pdf"
                >
                    <div>
                        <Dragger {...getUploadProps("pdf")}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag PDF files to this area to upload
                            </p>
                            <p className="ant-upload-hint">Support for multiple PDF uploads.</p>
                        </Dragger>
                    </div>
                </TabPane>
                <TabPane
                    tab={
                        <span>
                            <FileTextOutlined />
                            RIS Files
                        </span>
                    }
                    key="ris"
                >
                    <div>
                        <Dragger {...getUploadProps("ris")}>
                            <p className="ant-upload-drag-icon">
                                <FileTextOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag RIS file to this area to upload
                            </p>
                            <p className="ant-upload-hint">Support for single RIS file upload.</p>
                        </Dragger>
                    </div>
                </TabPane>
                <TabPane
                    tab={
                        <span>
                            <FileExcelOutlined />
                            Excel Files
                        </span>
                    }
                    key="excel"
                >
                    <div>
                        <Dragger {...getUploadProps("excel")}>
                            <p className="ant-upload-drag-icon">
                                <FileExcelOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag Excel file to this area to upload
                            </p>
                            <p className="ant-upload-hint">Support for single Excel file upload.</p>
                        </Dragger>
                    </div>
                </TabPane>
            </Tabs>
        </Modal>
    );
});

export default CitationBulkUploadModal;
